import { getAddressPointerForEvent } from "applesauce-core/helpers";
import { getProfilePointersFromList } from "applesauce-common/helpers";
import { useActiveAccount } from "applesauce-react/hooks";
import { Filter, NostrEvent, kinds } from "nostr-tools";
import { AddressPointer, ProfilePointer } from "nostr-tools/nip19";
import { PropsWithChildren, createContext, useCallback, useContext, useMemo } from "react";

import { DEFAULT_ANON_PUBKEY } from "../../const";
import useReplaceableEvent from "../../hooks/use-replaceable-event";
import useRouteSearchValue from "../../hooks/use-route-search-value";

export type ListId = "following" | "global" | "self" | string;

export type PeopleListContextType = {
  selected: ListId;
  listId?: string;
  pointer?: AddressPointer;
  listEvent?: NostrEvent;
  people: ProfilePointer[] | undefined;
  setSelected: (list: ListId) => void;
  filter: Filter | undefined;
};
const PeopleListContext = createContext<PeopleListContextType>({
  setSelected: () => {},
  people: undefined,
  selected: "global",
  filter: undefined,
});

export function usePeopleListContext() {
  return useContext(PeopleListContext);
}

function useListCoordinate(listId: ListId) {
  const account = useActiveAccount();

  return useMemo(() => {
    if (listId === "following") return account ? `${kinds.Contacts}:${account.pubkey}` : undefined;
    if (listId === "self") return undefined;
    if (listId === "global") return undefined;
    return listId;
  }, [listId, account]);
}

export function usePeopleListSelect(selected: ListId, onChange: (list: ListId) => void): PeopleListContextType {
  const account = useActiveAccount();

  const listId = useListCoordinate(selected);
  const event = useReplaceableEvent(listId);
  const pointer = useMemo(() => (event ? getAddressPointerForEvent(event) : undefined), [event]);
  const people = useMemo(() => event && getProfilePointersFromList(event), [event]);

  const filter = useMemo<Filter | undefined>(() => {
    if (selected === "global") {
      // If logged in, show all events (original global behavior)
      if (account) return {};
      // If logged out, should not reach here - should default to anon contact list
      return {};
    }
    if (selected === "self") {
      if (account) return { authors: [account.pubkey] };
      else return undefined;
    }
    if (!people || people.length === 0) return undefined;

    return { authors: people.map((p) => p.pubkey) };
  }, [people, selected, account]);

  return {
    people,
    selected,
    listId,
    pointer: pointer || undefined, // v5: convert null to undefined
    listEvent: event,
    setSelected: onChange,
    filter,
  };
}

export type PeopleListProviderProps = PropsWithChildren & {
  initList?: ListId;
};
export default function PeopleListProvider({ children, initList }: PeopleListProviderProps) {
  const account = useActiveAccount();
  const peopleParam = useRouteSearchValue("people");

  // When logged out and no selection, default to anon contact list instead of "global"
  const defaultSelected = useMemo(() => {
    if (initList) return initList as ListId;
    if (account) return "following";
    // Default to anon contact list when logged out
    return `${kinds.Contacts}:${DEFAULT_ANON_PUBKEY}`;
  }, [account, initList]);

  const selected = peopleParam.value || defaultSelected;
  const setSelected = useCallback(
    (value: ListId) => {
      peopleParam.setValue(value);
    },
    [peopleParam.setValue],
  );

  const context = usePeopleListSelect(selected, setSelected);

  return <PeopleListContext.Provider value={context}>{children}</PeopleListContext.Provider>;
}
